import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({ email: z.string().trim().email().max(255) });

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  orgId: string;
  orgName: string;
  onSent: () => void;
};

export default function InviteTeammateDialog({ open, onOpenChange, orgId, orgName, onSent }: Props) {
  const { user } = useAuth();
  const [email, setEmail] = useState("");

  const send = useMutation({
    mutationFn: async () => {
      const parsed = schema.safeParse({ email });
      if (!parsed.success) throw new Error("Please enter a valid email");
      if (!user?.id) throw new Error("Sign in required");

      const { data: inserted, error } = await supabase
        .from("partner_org_invites")
        .insert({
          partner_org_id: orgId,
          email: parsed.data.email,
          invited_by: user.id,
          status: "pending",
        })
        .select("id")
        .single();
      if (error) throw error;

      const { error: fnErr } = await supabase.functions.invoke("send-partner-invite", {
        body: { inviteId: inserted.id },
      });
      if (fnErr) {
        // Don't fail the whole flow; the row exists and can be resent.
        toast.warning("Invite created, but email failed to send. Use 'Resend' to retry.");
      }
    },
    onSuccess: () => {
      toast.success("Invite sent");
      setEmail("");
      onSent();
      onOpenChange(false);
    },
    onError: (e: any) => toast.error(e.message ?? "Failed to invite"),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Invite teammate</DialogTitle>
          <DialogDescription>
            They'll get an email with a link to join {orgName} as a partner admin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teammate@example.com" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => send.mutate()} disabled={send.isPending}>
            {send.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Send invite
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}